<script>
import { computed, defineComponent } from 'vue'
import SelectedPlanCard from "components/plans/cards/SelectedPlanCard";
import ServiceLocationCard from "components/service_location/ServiceLocationCard";
import ServiceStartDateCard from "components/service_start_date/ServiceStartDateCard";
import SelectedPlan from "src/composables/SelectedPlan";
import Plans from "src/composables/Plans";
import ProviderDetailsCard from "components/brand/ProviderDetailsCard";
import NeedHelpCard from "components/order/NeedHelpCard";
import ServiceLocation from "src/composables/ServiceLocation";
import CustomerInformation         from "src/composables/CustomerInformation";
import ServiceStartDate            from "src/composables/ServiceStartDate";
import StandardLayout              from "layouts/StandardLayout";
import UsageFilters                from "src/composables/UsageFilters";
import YourInfoCard                from "components/order/YourInfoCard";
import GetOrderLinkModal           from "components/GetOrderLinkModal";
import Utilities                   from "src/composables/Utilities";
import { useRoute, useRouter }     from "vue-router";
import Orders                      from "src/composables/Orders";
import Supabase                    from "src/composables/Supabase";
import Affiliates                  from "src/composables/Affiliates";
import { copyToClipboard, Notify } from "quasar";

export default defineComponent({
  name: 'OrderLayout',
  components: {
    GetOrderLinkModal,
    YourInfoCard,
    StandardLayout,
    NeedHelpCard,
    ProviderDetailsCard,
    ServiceStartDateCard,
    ServiceLocationCard,
    SelectedPlanCard
  },
  props: {
    step: {
      type: String,
      enum: [
        'address',
        'date',
        'info'
      ]
    }
  },
  setup() {
    const { order } = Orders();
    const { selected_plan } = SelectedPlan();
    const { usage_selected } = UsageFilters();
    const { service_location, owns_property } = ServiceLocation();
    const { action, selected_start_date } = ServiceStartDate();
    const { first_name, last_name, phone_number, email, spanish } = CustomerInformation();
    const { plan_map } = Plans();
    const { generate_affiliate_link } = Affiliates()

    selected_plan.value = plan_map(selected_plan.value);
    const {
      plan_support_phone_number,
      plan_hours_string,
      plan_brand_puct,
      plan_brand_legal_name,
      plan_brand_address,
      plan_brand_support_email,
      plan_brand_website
    } = Utilities();

    const { is_authenticated, user: supabase_user } = Supabase()

    const puct_number = plan_brand_puct(selected_plan.value);
    const address = plan_brand_address(selected_plan.value);
    const support_email = plan_brand_support_email(selected_plan.value);
    const website = plan_brand_website(selected_plan.value);
    const name = plan_brand_legal_name(selected_plan.value);
    const support_phone_number = plan_support_phone_number(selected_plan.value);

    const provider_info = computed(() => {
      const details = [name, puct_number && `PUCT Certificate #${puct_number}`, address, support_phone_number, website, support_email]
      return `Provider Details: ${details.filter(x => !!x).join(' | ')}`
    })

    const route = useRoute();


    const base_tabs = [
      { icon: 'pin_drop', to: '/order/service_location', label: 'Service Location', disabled: false },
      { icon: 'event', to: '/order/start_date', label: 'Service Start Date', disabled: true },
      { icon: 'account_circle', to: '/order/customer_information', label: 'Customer Information', disabled: true },
      { icon: 'summarize', to: '/order/authorizations', label: 'Review', disabled: true }
    ]

    const alternate_tabs = [
      { icon: 'error', to: '/order/error', label: 'Errors', disabled: true },
      { icon: 'handshake', to: '/order/agreements', label: 'Agreements', disabled: true },
      { icon: 'payments', to: '/order/deposit_payment', label: 'Deposit Payment', disabled: true },
      { icon: 'request_quote', to: '/order/deposit_options', label: 'Deposit Options', disabled: true },
      { icon: 'price_check', to: '/order/review_deposit_option', label: 'Review Deposit', disabled: true }
    ]

    const route_tabs = computed(() => {
      let disabled = false;
      const path = route.path;
      const found = alternate_tabs.find(x => x.to === order.value?.processed_path);
      const copy = [...base_tabs];
      if (found) {
        copy.push(found);
      }
      for (const item of copy) {
        item.disabled = disabled;
        if (path === item.to) {
          disabled = true;
        }
      }
      copy.push({ icon: 'check', label: 'Complete', disabled: true })
      return copy;
    })

    const generate_affiliate_order_link = async function () {
      const link = generate_affiliate_link(supabase_user);
      copyToClipboard(link).then(() => {
        Notify.create({
          message: 'Your link is copied to the clipboard',
          color: 'positive',
          actions: [
            { label: 'Dismiss', color: 'white' }
          ]
        })
      })
    }

    return {
      first_name,
      last_name,
      email,
      spanish,
      phone_number,
      action,
      selected_start_date,
      selected_plan,
      usage_selected,
      service_location,
      owns_property,
      provider_info,
      route_tabs,
      generate_affiliate_order_link,
      is_authenticated,
      supabase_user
    }
  }
})
</script>

<template>
  <standard-layout>
    <template v-slot:header_top>
      <section class="bg-secondary q-pa-sm text-center" v-if="is_authenticated && supabase_user?.user_metadata?.affiliate?.cp_afid">
        <q-btn color="white" no-caps
               class="text-bold rounded-borders-8 text-black"
               unelevated @click="generate_affiliate_order_link" label="Create Affiliate Link">
        </q-btn>
      </section>
    </template>
    <template v-slot:main @keydown.enter="e => e.preventDefault()">

      <q-page padding class="bg-blue-grey-1">

        <div class="row justify-center">
          <div class="col-12 col-xl-8 col-lg-10">
            <q-tabs class="q-mb-md bg-grey-2 q-card--bordered" active-color="primary" inline-label
                    align="justify">
              <q-route-tab v-for="route_tab in route_tabs" :icon="route_tab.icon" :to="route_tab.to"
                           :label="route_tab.label" :disable="route_tab.disabled" no-caps></q-route-tab>
            </q-tabs>

            <div class="row q-gutter-y-md">
              <div class="col-12 col-md-8">
                <div class="q-mr-md-md q-mr-none">
                  <slot></slot>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="q-gutter-y-md">
                  <selected-plan-card v-if="selected_plan" :plan="selected_plan" :usage="usage_selected"
                                      :show_edit="true"></selected-plan-card>
                  <your-info-card :first_name="first_name" :last_name="last_name" :email="email" :action="action"
                                  :spanish="spanish"
                                  :phone_number="phone_number" :service_location_address="service_location?.label"
                                  :service_location_id="service_location?.esiid"
                                  :selected_start_date="selected_start_date" :show_edit="true"></your-info-card>
                  <need-help-card v-if="selected_plan" :plan="selected_plan"></need-help-card>
                </div>
              </div>
            </div>
          </div>
        </div>

      </q-page>

    </template>

    <template v-slot:footer>
      <div class="bg-blue-grey-1 q-px-md q-pt-xl">
        <div class="text-center">
          {{ provider_info }}
        </div>
      </div>

    </template>

    <template v-slot:post_footer>
      <div class="text-center bg-blue-grey-1">
        <get-order-link-modal></get-order-link-modal>
      </div>
    </template>

  </standard-layout>

</template>

<style>
a {
  font-weight: bold;
  color: #29b6f6;
}

.q-stepper__content {
  display: none;
}
</style>
